// Legacy console deep links (ADR-080, ui-spec §2.8). Org-less legacy URLs go to the last-used org and its default
// project; with none, the org chooser keeps the target and continues after the pick, so a redirect is a function of
// the chosen org and project.

export interface LegacyRedirect {
  /** Whether the target needs an org (and project) chosen first. */
  needsOrg: boolean;
  to: (org: string, project: string) => string;
}

const VIEW_PAGES: Record<string, { path: string; scope: "project" | "org" | "user" }> = {
  "compute-list": { path: "runs", scope: "project" },
  capacity: { path: "runs?tab=gpus", scope: "project" },
  instances: { path: "workspaces", scope: "project" },
  workloads: { path: "runs?kind=TrainingJob", scope: "project" },
  "new-run": { path: "jobs/new", scope: "project" },
  environments: { path: "environments", scope: "project" },
  inference: { path: "inference", scope: "project" },
  workspaces: { path: "workspaces", scope: "project" },
  sandboxes: { path: "sandboxes", scope: "project" },
  "agent-deployments": { path: "sandboxes", scope: "project" },
  billing: { path: "billing/overview", scope: "org" },
  connections: { path: "connections", scope: "project" },
  agents: { path: "integrations/mcp", scope: "org" },
  team: { path: "team/members", scope: "org" },
  keys: { path: "settings/apikeys", scope: "org" },
  settings: { path: "/account/profile", scope: "user" },
  compute: { path: "pools", scope: "project" },
  insights: { path: "overview", scope: "project" },
  activity: { path: "settings/audit", scope: "org" },
  benchmarks: { path: "runs", scope: "project" },
};

const e = encodeURIComponent;

function fixed(path: string): LegacyRedirect {
  return { needsOrg: false, to: () => path };
}

function scoped(path: string, scope: "project" | "org" | "user"): LegacyRedirect {
  if (scope === "user") return fixed(path);
  return {
    needsOrg: true,
    to: (org, project) => (scope === "org" ? `/${e(org)}/${path}` : `/${e(org)}/${e(project)}/${path}`),
  };
}

function moved(type: string, id: string): LegacyRedirect {
  return fixed(`/moved?legacy=${e(`${type}:${id}`)}`);
}

/** Maps a legacy console URL to its new route, or undefined when it is not a legacy shape. */
export function legacyRedirect(url: URL): LegacyRedirect | undefined {
  const path = url.pathname.replace(/^\/console(?=\/|$)/, "") || "/";
  const q = url.searchParams;

  const invite = /^#invite=(.+)$/.exec(url.hash);
  if (invite) return fixed(`/invite/${e(decodeURIComponent(invite[1]!))}`);

  if (q.has("auth_state") && q.has("code")) {
    return fixed(`/auth/callback?code=${e(q.get("code")!)}&state=${e(q.get("auth_state")!)}`);
  }

  if (path === "/device" || path === "/device/") return fixed("/cli-auth/device");

  const consent = /^\/agent-authorize\/([^/]+)\/?$/.exec(path);
  if (consent) return fixed(`/oauth/consent?authorization_id=${e(decodeURIComponent(consent[1]!))}`);

  const sandboxPath = /^\/sandboxes\/([^/]+)\/?$/.exec(path);
  if (sandboxPath) return moved("sandbox", decodeURIComponent(sandboxPath[1]!));

  if (path !== "/") return undefined;

  if (q.get("run")) return moved("run", q.get("run")!);
  if (q.get("sandbox")) return moved("sandbox", q.get("sandbox")!);
  if (q.get("workspace")) return moved("workspace", q.get("workspace")!);
  if (q.get("instance")) return moved("instance", q.get("instance")!);
  if (q.get("payment_method") === "return") return scoped("billing/overview?setup=legacy", "org");
  if (q.get("left_team") === "1") return fixed("/?notice=left-org");

  const view = q.get("view");
  if (!view) return undefined;

  if (view === "compute-list" && (q.get("type") === "training" || q.has("tab"))) {
    const params = new URLSearchParams();
    if (q.get("type") === "training") params.set("kind", "TrainingJob");
    const tab = q.get("tab");
    if (tab) params.set("tab", tab === "running" ? "active" : tab);
    return scoped(`runs?${params.toString()}`, "project");
  }
  if (view === "environments" && q.get("environment")) {
    const recipe = q.get("recipe");
    return {
      needsOrg: true,
      to: (org) => `/${e(org)}/nodus/environments/${e(q.get("environment")!)}${recipe ? `?configure=${e(recipe)}` : ""}`,
    };
  }
  if (view === "sandboxes" && q.get("agent")) return moved("agent", q.get("agent")!);
  if (view === "sandboxes" && q.get("group")) return moved("group", q.get("group")!);

  const page = VIEW_PAGES[view];
  return page ? scoped(page.path, page.scope) : undefined;
}
